import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, Upload, Search, Cpu, FileCheck, AlertTriangle,
  Download, CheckCircle, Database, FileText, Shield,
  BarChart2, Layers, Zap, GitMerge, Package, ScanLine
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';
import ScreenshotSlider from '../components/ScreenshotSlider';

const BOQ_SLIDES = [
  '/mockup/boq/screen 1.png',
  '/mockup/boq/screen 2.png',
  '/mockup/boq/screen 3.png',
  '/mockup/boq/screen 4.png',
  '/mockup/boq/screen 5.png',
  '/mockup/boq/screen 6.png',
];

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function Badge({ children, variant = 'primary' }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase';
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    accent: 'bg-accent/15 text-amber-700 border border-accent/30',
    dark: 'bg-white/10 text-white/80 border border-white/20',
  };
  return <span className={`${base} ${variants[variant]}`}>{children}</span>;
}

const WORKFLOW_STEPS = [
  { icon: Upload, labelEn: 'Upload BoQ', labelAr: 'رفع الملف', descEn: 'Excel, PDF, Word, Scanned', descAr: 'Excel, PDF, Word, مسح ضوئي', accent: false },
  { icon: Search, labelEn: 'Extract Rows', labelAr: 'استخراج البنود', descEn: 'AI row detection', descAr: 'كشف صفوف بالذكاء الاصطناعي', accent: false },
  { icon: Layers, labelEn: 'Normalize Units', labelAr: 'تنسيق الوحدات', descEn: 'Standard unit mapping', descAr: 'ربط الوحدات المعيارية', accent: false },
  { icon: Cpu, labelEn: 'Classify Product', labelAr: 'تصنيف المنتج', descEn: 'Category + attribute tagging', descAr: 'تصنيف وإضافة سمات', accent: false },
  { icon: GitMerge, labelEn: 'Map to Catalog', labelAr: 'ربط بالكتالوج', descEn: 'Confidence scoring', descAr: 'تقييم الثقة', accent: false },
  { icon: AlertTriangle, labelEn: 'Flag Exceptions', labelAr: 'إبراز الاستثناءات', descEn: 'Review queue for low confidence', descAr: 'طابور مراجعة المنخفض ثقة', accent: true },
  { icon: Download, labelEn: 'Export ERP Sheet', labelAr: 'تصدير لـ ERP', descEn: 'Odoo, SAP, Oracle, Dynamics', descAr: 'Odoo, SAP, Oracle, Dynamics', accent: false },
];

export default function BoQSolution() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const problems = [
    {
      icon: FileText,
      titleEn: 'Inconsistent BoQ Formats',
      titleAr: 'صيغ قوائم كميات غير موحدة',
      descEn: 'Every supplier and project delivers BoQs in a different layout, column order, and unit system — impossible to process at scale.',
      descAr: 'كل مورد ومشروع يسلّم قوائم كميات بتخطيط مختلف وترتيب أعمدة مختلف — مستحيل المعالجة على نطاق واسع.',
      color: 'text-red-500', bg: 'bg-red-50',
    },
    {
      icon: BarChart2,
      titleEn: 'Manual Classification Errors',
      titleAr: 'أخطاء التصنيف اليدوي',
      descEn: 'Engineers manually categorize thousands of line items per project, introducing errors, inconsistencies, and hours of rework.',
      descAr: 'يصنّف المهندسون آلاف البنود يدوياً لكل مشروع مما يُدخل أخطاء وساعات من إعادة العمل.',
      color: 'text-amber-500', bg: 'bg-amber-50',
    },
    {
      icon: Database,
      titleEn: 'ERP Data Not Ready',
      titleAr: 'بيانات ERP غير جاهزة',
      descEn: 'Raw BoQ data never matches ERP field requirements — requiring painful manual re-entry and reconciliation before import.',
      descAr: 'بيانات قوائم الكميات الخام لا تتطابق مع متطلبات حقول ERP مما يستوجب إدخالاً يدوياً مؤلماً.',
      color: 'text-blue-500', bg: 'bg-sky-50',
    },
    {
      icon: Shield,
      titleEn: 'No Traceability',
      titleAr: 'لا قابلية للتتبع',
      descEn: 'No audit trail for mapping decisions means disputes take days to resolve and compliance checks always start from scratch.',
      descAr: 'لا سجل مراجعة لقرارات الربط مما يعني أن النزاعات تستغرق أياماً وفحوصات الامتثال تبدأ من الصفر.',
      color: 'text-muted', bg: 'bg-white',
    },
  ];

  const inputs = [
    { icon: FileText, label: 'Excel', desc: lang === 'ar' ? '.xls, .xlsx متعدد الأوراق' : '.xls, .xlsx multi-sheet', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { icon: FileCheck, label: 'PDF', desc: lang === 'ar' ? 'ملفات PDF نصية وجداول' : 'Native PDF text & tables', color: 'text-red-500', bg: 'bg-red-50' },
    { icon: FileText, label: 'Word', desc: lang === 'ar' ? 'ملفات .doc و .docx' : '.doc & .docx files', color: 'text-blue-600', bg: 'bg-blue-50' },
    { icon: ScanLine, label: lang === 'ar' ? 'جداول ممسوحة' : 'Scanned Tables', desc: lang === 'ar' ? 'صور JPEG/PNG/TIFF بـ OCR' : 'JPEG/PNG/TIFF with OCR', color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  const mappingFeatures = [
    lang === 'ar' ? 'تقييم ثقة على مستوى البند (مرتفع / منخفض / استثناء)' : 'Per-line confidence scoring (High / Low / Exception)',
    lang === 'ar' ? 'قواعد تشابه نصي وقواعد هندسية مخصصة' : 'Custom fuzzy-match and geometric rules per catalog',
    lang === 'ar' ? 'قواعد حجم قابلة للتكوين (عرض×ارتفاع، قطر، فتحات)' : 'Configurable sizing rules (WxH, H-only, Diameter, Slots)',
    lang === 'ar' ? 'تعلم ديناميكي من قرارات المراجعة البشرية' : 'Dynamic learning from human review decisions',
    lang === 'ar' ? 'دعم كتالوجات متعددة لموردين مختلفين' : 'Multi-catalog support across different suppliers',
  ];

  const sizingRules = [
    { rule: 'W × H',     example: '600 × 300', img: '/mockup/boq/sizes/wh.webp',    descEn: 'Width and height dimensions (e.g., grilles, diffusers)', descAr: 'أبعاد العرض والارتفاع (مثل شبكات، موزعات)' },
    { rule: 'H-only',    example: '600H',       img: '/mockup/boq/sizes/h.png',      descEn: 'Height-based sizing for vertical components',            descAr: 'القياس بالارتفاع للمكونات الرأسية'             },
    { rule: 'Diameter',  example: 'Ø200',       img: '/mockup/boq/sizes/dia.webp',   descEn: 'Circular duct and fitting sizing',                       descAr: 'قياس المجاري والتوصيلات الدائرية'              },
    { rule: 'Slots + L', example: '65 × 1200',  img: '/mockup/boq/sizes/slots.webp', descEn: 'Slot count and length for linear diffusers',             descAr: 'عدد الفتحات والطول للموزعات الخطية'            },
  ];

  const outputs = [
    { icon: Database, titleEn: 'ERP-Ready Sheet', titleAr: 'ورقة ERP جاهزة', descEn: 'Mapped, normalized, import-ready for your ERP system', descAr: 'مربوطة ومعيارية وجاهزة للاستيراد لنظام ERP', color: 'text-primary', bg: 'bg-primary/8' },
    { icon: AlertTriangle, titleEn: 'Exception Report', titleAr: 'تقرير الاستثناءات', descEn: 'All flagged items requiring human review, structured', descAr: 'جميع البنود المُعلَّمة تتطلب مراجعة بشرية، منظمة', color: 'text-amber-500', bg: 'bg-amber-50' },
    { icon: CheckCircle, titleEn: 'Audit Trail Log', titleAr: 'سجل المراجعة', descEn: 'Every mapping decision with confidence score and timestamp', descAr: 'كل قرار ربط مع تقييم الثقة والطابع الزمني', color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { icon: Package, titleEn: 'Catalog Delta', titleAr: 'فجوة الكتالوج', descEn: 'Items with no catalog match — flagged for catalog expansion', descAr: 'بنود بلا مطابق في الكتالوج — مُعلَّمة لتوسيع الكتالوج', color: 'text-blue-500', bg: 'bg-sky-50' },
  ];

  const erpPlatforms = ['Odoo', 'SAP', 'Oracle', 'MS Dynamics'];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <AIAssistant source="boq" />

      {/* ── HERO ── */}
      <section className="relative min-h-screen overflow-hidden bg-midnight">
        <div className="absolute inset-0 blueprint-grid-dark opacity-50" />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-primary/12 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[300px] rounded-full bg-accent/6 blur-[100px] pointer-events-none" />

        <div className={`relative z-10 flex min-h-screen ${isRTL ? 'flex-row-reverse' : ''}`}>

          {/* ── Left: text content ── */}
          <div className="flex-1 flex items-center px-6 sm:px-10 lg:px-16 pt-24 pb-16">
            <div className="max-w-xl w-full">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <Badge variant="dark"><FileText size={12} />{lang === 'ar' ? 'الوحدة ١' : 'Module 1'}</Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-6 text-4xl sm:text-5xl lg:text-[3rem] font-black text-white leading-[1.06] tracking-tight"
              >
                {lang === 'ar' ? (
                  <>من قوائم كميات <span className="gradient-text">غير منظمة</span> إلى مخرجات ERP جاهزة</>
                ) : (
                  <>From <span className="gradient-text">Unstructured</span> BoQs to ERP-Ready Outputs</>
                )}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed"
              >
                {lang === 'ar'
                  ? 'ارفع أي ملف قائمة كميات. يستخرج نظام تولي البنود تلقائياً، يصنّفها، يربطها بكتالوجك، ويصدّر ورقة ERP جاهزة — في دقائق لا أيام.'
                  : 'Upload any BoQ file. Tuly extracts line items automatically, classifies them, maps to your catalog, and exports an ERP-ready sheet — in minutes, not days.'}
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                className="mt-8 flex flex-wrap gap-8"
              >
                {[
                  { val: '85%', label: lang === 'ar' ? 'توفير في الوقت' : 'Time saved' },
                  { val: '99%', label: lang === 'ar' ? 'دقة استخراج البنود' : 'Row extraction accuracy' },
                  { val: '4', label: lang === 'ar' ? 'تكاملات ERP' : 'ERP integrations' },
                ].map(({ val, label }, i) => (
                  <div key={i}>
                    <p className="text-3xl font-black gradient-text">{val}</p>
                    <p className="text-white/45 text-xs mt-0.5">{label}</p>
                  </div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                <button
                  onClick={() => navigate('/contact')}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
                >
                  {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                  <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
                </button>
                <button
                  onClick={() => navigate('/solutions')}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/8 text-white font-semibold text-sm hover:bg-white/14 transition-all border border-white/20"
                >
                  {lang === 'ar' ? 'كل الحلول' : 'All Solutions'}
                </button>
              </motion.div>
            </div>
          </div>

          {/* ── Right: full-height slider ── */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="hidden xl:block w-[52%] relative"
          >
            <ScreenshotSlider
              images={BOQ_SLIDES}
              interval={3200}
              alt="BoQ Interface"
              cover
              className="absolute inset-0"
            />
            {/* fade edge toward text */}
            <div className={`absolute inset-y-0 ${isRTL ? 'right-0' : 'left-0'} w-20 bg-gradient-to-r ${isRTL ? 'from-transparent to-midnight' : 'from-midnight to-transparent'} z-10 pointer-events-none`} />
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 z-20"
        >
          <span className="text-white/25 text-[10px] tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="w-px h-8 bg-gradient-to-b from-white/25 to-transparent"
          />
        </motion.div>
      </section>

      {/* ── PROBLEM SECTION ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><AlertTriangle size={12} />{lang === 'ar' ? 'المشكلة' : 'The Problem'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'لماذا قوائم الكميات مؤلمة جداً' : 'Why BoQ Processing Is So Painful'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar'
                ? 'فرق المشتريات والهندسة تخسر أياماً كل أسبوع في التعامل مع فوضى قوائم الكميات.'
                : 'Procurement and engineering teams lose days every week wrestling with BoQ chaos.'}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {problems.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${p.bg} flex items-center justify-center mb-4`}>
                  <p.icon size={18} className={p.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{lang === 'ar' ? p.titleAr : p.titleEn}</h3>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? p.descAr : p.descEn}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── INPUT FORMATS ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Upload size={12} />{lang === 'ar' ? 'صيغ الإدخال' : 'Accepted Input Formats'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-heading">
              {lang === 'ar' ? 'ارفع ما لديك — نتولى الباقي' : 'Upload What You Have — We Handle the Rest'}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {inputs.map((inp, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-4 sm:p-6 border border-border-light card-hover text-center"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${inp.bg} flex items-center justify-center mx-auto mb-3 sm:mb-4`}>
                  <inp.icon size={22} className={inp.color} />
                </div>
                <h3 className="text-heading font-bold text-base mb-1">{inp.label}</h3>
                <p className="text-body text-xs">{inp.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── 7-STEP WORKFLOW ── */}
      <Section className="py-24 bg-midnight relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-40" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] rounded-full bg-primary/10 blur-[100px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="dark"><Zap size={12} />{lang === 'ar' ? 'سير العمل' : 'Workflow'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-white">
              {lang === 'ar' ? '٧ خطوات من الرفع إلى التصدير' : '7 Steps from Upload to Export'}
            </h2>
            <p className="mt-3 text-white/55 text-sm">
              {lang === 'ar' ? 'كل خطوة آلية بالكامل. الخطوة ٦ هي بوابة المراجعة البشرية للاستثناءات.' : 'Every step fully automated. Step 6 is the human review gate for exceptions.'}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
            {WORKFLOW_STEPS.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`relative rounded-2xl p-3 sm:p-4 border flex flex-col items-center text-center ${
                  step.accent
                    ? 'border-accent/50 bg-accent/12 ring-2 ring-accent/25'
                    : 'border-dark-border bg-deep-surface/50'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${step.accent ? 'bg-accent/20' : 'bg-primary/10'}`}>
                  <step.icon size={18} className={step.accent ? 'text-accent' : 'text-primary'} />
                </div>
                <div className={`text-[10px] font-bold uppercase tracking-wide mb-1 ${step.accent ? 'text-accent' : 'text-primary/60'}`}>
                  {lang === 'ar' ? `خطوة ${i + 1}` : `Step ${i + 1}`}
                </div>
                <p className={`text-xs font-semibold ${step.accent ? 'text-accent' : 'text-white'}`}>
                  {lang === 'ar' ? step.labelAr : step.labelEn}
                </p>
                <p className="text-[10px] text-muted mt-1 leading-snug">{lang === 'ar' ? step.descAr : step.descEn}</p>

                {step.accent && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-accent text-[#1A1A1A] text-[9px] font-black uppercase whitespace-nowrap">
                    {lang === 'ar' ? 'بوابة مراجعة' : 'Review Gate'}
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── MAPPING INTELLIGENCE ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="primary"><Cpu size={12} />{lang === 'ar' ? 'ذكاء الربط' : 'Mapping Intelligence'}</Badge>
              <h2 className="mt-4 text-3xl font-black text-heading">
                {lang === 'ar' ? 'ربط ذكي يتعلم من قراراتك' : 'Smart Mapping That Learns from Your Decisions'}
              </h2>
              <p className="mt-3 text-body text-sm leading-relaxed">
                {lang === 'ar'
                  ? 'المحرك لا يطابق فحسب — بل يقيّم مستوى ثقته في كل بند ويوجّه الاستثناءات للمراجعة البشرية تلقائياً.'
                  : "The engine doesn't just match — it scores confidence on every line item and automatically routes exceptions to human review."}
              </p>
              <ul className="mt-6 space-y-3">
                {mappingFeatures.map((f, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle size={15} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-body text-sm">{f}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Screen mockup */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden border border-border-light shadow-card-hover">
                <img src="/mockup/boq/screen 2.png" alt="Mapping Interface" className="w-full" />
              </div>
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className={`absolute -bottom-4 ${isRTL ? 'left-4' : 'right-4'} bg-white rounded-xl shadow-card-hover p-3 border border-border-light`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-heading font-semibold text-xs">{lang === 'ar' ? 'ثقة مرتفعة: ٩٤٪' : 'High Confidence: 94%'}</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ── SIZING RULES ── */}
      <Section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="accent"><Layers size={12} />{lang === 'ar' ? 'قواعد القياس' : 'Sizing Rules'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-heading">
              {lang === 'ar' ? 'قواعد قياس لكل نوع من المنتجات' : 'Sizing Rules for Every Product Type'}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sizingRules.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.55 }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                className="bg-white rounded-2xl overflow-hidden border-2 border-amber-200/70 shadow-sm hover:shadow-lg transition-shadow duration-300 group cursor-default"
              >
                {/* Image panel */}
                <div className="relative bg-slate-50/80 flex items-center justify-center px-6 pt-8 pb-6 min-h-[200px]">
                  {/* Corner badge */}
                  <div className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-primary flex items-center justify-center shadow-sm">
                    <Layers size={13} className="text-white" />
                  </div>
                  <img
                    src={r.img}
                    alt={r.rule}
                    className="max-h-[150px] w-auto object-contain group-hover:scale-[1.06] transition-transform duration-500 drop-shadow-md"
                  />
                </div>

                {/* Text panel */}
                <div className="px-5 py-5 text-center border-t border-amber-100">
                  <h3 className="text-primary font-black text-xl mb-1 tracking-tight">{r.rule}</h3>
                  <p className="text-amber-500 font-bold text-base font-mono mb-3">{r.example}</p>
                  <p className="text-gray-500 text-sm leading-relaxed">{lang === 'ar' ? r.descAr : r.descEn}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── ERP INTEGRATION ── */}
      <Section className="py-20 bg-midnight relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-primary/10 blur-[100px] rounded-full" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge variant="dark"><Database size={12} />{lang === 'ar' ? 'تكامل ERP' : 'ERP Integration'}</Badge>
          <h2 className="mt-4 text-3xl font-black text-white">
            {lang === 'ar' ? 'يتصل بأنظمة ERP الكبرى' : 'Connects to Major ERP Systems'}
          </h2>
          <p className="mt-3 text-white/55 text-sm max-w-lg mx-auto">
            {lang === 'ar'
              ? 'مخرجات موحدة جاهزة للاستيراد المباشر في Odoo أو SAP أو Oracle أو Microsoft Dynamics.'
              : 'Standardized output ready for direct import into Odoo, SAP, Oracle, or Microsoft Dynamics.'}
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {erpPlatforms.map((erp, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="px-6 py-3 rounded-xl bg-deep-surface border border-dark-border text-white font-semibold text-sm dark-card-hover"
              >
                {erp}
              </motion.div>
            ))}
          </div>

          <div className="mt-8 max-w-xl mx-auto bg-deep-surface/50 border border-dark-border rounded-2xl p-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              {[
                { val: lang === 'ar' ? 'فوري' : 'Instant', label: lang === 'ar' ? 'استيراد مباشر' : 'Direct import' },
                { val: '0', label: lang === 'ar' ? 'إعادة إدخال يدوي' : 'Manual re-entry' },
                { val: '100%', label: lang === 'ar' ? 'توافق الحقول' : 'Field compatibility' },
              ].map((s, i) => (
                <div key={i}>
                  <p className="text-2xl font-black gradient-text">{s.val}</p>
                  <p className="text-white/40 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── OUTPUTS ── */}
      <Section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <Badge variant="primary"><Download size={12} />{lang === 'ar' ? 'المخرجات' : 'Outputs'}</Badge>
            <h2 className="mt-4 text-3xl font-black text-heading">
              {lang === 'ar' ? 'ما تحصل عليه في النهاية' : 'What You Get at the End'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {outputs.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover"
              >
                <div className={`w-10 h-10 rounded-xl ${o.bg} flex items-center justify-center mb-4`}>
                  <o.icon size={18} className={o.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{lang === 'ar' ? o.titleAr : o.titleEn}</h3>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? o.descAr : o.descEn}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── FINAL CTA ── */}
      <Section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-50" />
        <div className="absolute top-0 right-1/3 w-72 h-72 rounded-full bg-primary/5 blur-[80px]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-black text-heading">
            {lang === 'ar' ? 'جاهز لتحويل عمليات قوائم الكميات؟' : 'Ready to Transform Your BoQ Operations?'}
          </h2>
          <p className="mt-4 text-body leading-relaxed text-sm max-w-xl mx-auto">
            {lang === 'ar'
              ? 'احجز عرضاً مباشراً وشاهد كيف يحوّل نظام تولي ملفات قوائم الكميات إلى مخرجات ERP جاهزة خلال دقائق.'
              : 'Book a live demo and see how Tuly transforms your BoQ files into ERP-ready outputs in minutes.'}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
            >
              {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
            <button
              onClick={() => navigate('/solutions')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border-2 border-border-light text-body font-semibold hover:border-primary/30 hover:text-primary transition-all"
            >
              {lang === 'ar' ? 'جميع الحلول' : 'All Solutions'}
            </button>
          </div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/tuly.png" alt="Tuly Technology" className="h-8 w-auto" />
                <span className="text-white font-bold text-sm">Tuly Technology</span>
              </div>
              <p className="text-muted text-xs leading-relaxed">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.solutions')}</h4>
              <ul className="space-y-2.5">
                {[['boq', '/solutions/boq'], ['submittals', '/solutions/submittals'], ['platform', '/platform']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.company')}</h4>
              <ul className="space-y-2.5">
                {[['about', '/about'], ['contact', '/contact']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.legal')}</h4>
              <ul className="space-y-2.5">
                {[['privacy', '/privacy'], ['terms', '/terms']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-dark-border flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
            <p className="text-muted/50 text-xs">{t('footer.madeWith')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
